import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { Handshake } from 'lucide-react'

interface Partner {
  name: string
  src: string
}

function PartnersMarquee({ partners }: { partners: Partner[] }) {
  const duplicatedPartners = [...partners, ...partners, ...partners, ...partners, ...partners, ...partners]

  return (
    <div className="relative w-screen left-1/2 -translate-x-1/2 overflow-hidden py-8 pb-16">
      {/* Gradient Overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-[var(--color-void)] via-[var(--color-void)] to-transparent z-20 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-[var(--color-void)] via-[var(--color-void)] to-transparent z-20 pointer-events-none" />

      {/* Marquee Track */}
      <motion.div
        className="flex gap-6 md:gap-8 items-center px-4 sm:px-6 lg:px-8"
        animate={{ x: ["0%", "-50%"] }}
        transition={{
          x: {
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          }
        }}
      >
        {duplicatedPartners.map((partner, index) => (
          <div
            key={`partner-${partner.name}-${index}`}
            className="flex-shrink-0 flex items-center justify-center px-8 md:px-12 bg-white rounded-xl border border-[var(--color-graphite)] hover:border-[var(--accent-primary)]/30 hover:shadow-lg hover:shadow-[var(--accent-primary)]/10 transition-all duration-500"
            style={{ width: '220px', height: '120px' }}
          >
            <img
              src={partner.src}
              alt={partner.name}
              className="max-h-16 md:max-h-20 w-auto object-contain transition-all duration-500"
              loading="lazy"
            />
          </div>
        ))}
      </motion.div>
    </div>
  )
}

export function PartnershipsSection() {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const partners: Partner[] = [
    { name: 'KUKA', src: '/media/logos/logo-kuka.png' },
    { name: 'KNX', src: '/media/logos/logo-knx.png' },
    { name: 'Sunpower', src: '/media/logos/logo-sunpower.png' },
    { name: 'Vimar', src: '/media/logos/vimar-logo.png' },
  ]

  return (
    <section ref={ref} className="section relative overflow-hidden bg-[var(--color-void)]">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, var(--color-fog) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
        }}
      />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12"
        >
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--accent-primary)]/10 mb-6">
            <Handshake className="h-8 w-8 text-[var(--accent-primary)]" />
          </div>

          <h2 className="headline-xl">
            {t('partnerships.title')}
          </h2>

          <p className="body-lg max-w-2xl mx-auto mt-4">
            {t('partnerships.p1')}
          </p>
        </motion.div>
      </div>

      {/* Partners Marquee */}
      <PartnersMarquee partners={partners} />

      {/* Bottom Spacer */}
      <div className="h-8 md:h-12" />
    </section>
  )
}
